import { useEffect, useMemo, useRef, useState } from 'react';

function prefersReducedMotion() {
  if (typeof window === 'undefined') return true;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
}

/**
 * Scroll-driven text slideshow.
 * As the user scrolls, we advance through slides and animate each new slide in.
 */
export default function ScrollTextRotator({ stepPx = 240 }) {
  const slides = useMemo(
    () => [
      {
        subtitle: 'Structured Learning, Better Results',
        body:
          'Access organized study materials by programme, module, and topic.\nPrepare effectively for licensing and end of semester exams.',
      },
      {
        subtitle: 'Study Notes + Deep Understanding',
        body:
          'Learn faster with clean, structured notes and practical examples.\nBuild confidence before major assessments.',
      },
      {
        subtitle: 'Practice Questions (CAT 1 & CAT 2)',
        body:
          'Build confidence with targeted practice questions, answer guides, and model responses.\nTry different versions and improve every time.',
      },
      {
        subtitle: 'Self-Evaluation + Progress Tracking',
        body:
          'See performance trends and weak areas early.\nTurn weak topics into strengths with consistent self-testing.',
      },
    ],
    []
  );

  const [index, setIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const reduce = prefersReducedMotion();

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = 0;
        const y = window.scrollY || document.documentElement.scrollTop || 0;
        const next = slides.length > 0 ? Math.floor(y / Math.max(80, stepPx)) % slides.length : 0;
        setIndex((prev) => {
          if (prev === next) return prev;
          if (!reduce) setAnimKey((k) => k + 1);
          return next;
        });
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, [slides, stepPx]);

  const slide = slides[index] || slides[0];
  const animate = prefersReducedMotion() ? '' : 'splash-rotate-in';

  return (
    <div className="splash-scroll-rotator" aria-live="polite">
      <div key={animKey} className={`splash-scroll-slide ${animate}`}>
        <p className="splash-subtitle">{slide.subtitle}</p>
        <p className="splash-description whitespace-pre-line">{slide.body}</p>
      </div>
    </div>
  );
}

